import React from "react";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import { axiosConfig } from "../../../../axios/axiosConfig";
import { addCommaToCurrency } from "../../../../utils/common/digitCommaSeparation";

const headCellSx = {
  fontWeight: 700,
  fontSize: "11px",
  color: "#fff",
  background: "#04343a",
  borderBottom: "none",
  padding: "10px 10px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const UserPaymentsDashboardTable = ({
  allUserPaymentDetails,
  setAllUserPaymentDetails,
  filterData,
  setToDate,
  setFromDate,
  setReload,
  toDate,
  fromDate,
  totalPageCount,
  totalAmount,
  totalCount,
  searchQuery,
  setSearchQuery,
  handleSearch,
  handleClearSearch,
}) => {
  const refundMutation = useMutation({
    mutationFn: async (paymentID) => {
      const res = await axiosConfig({
        method: "post",
        url: "/payment/refund",
        data: { id: paymentID },
      });
      return res.data;
    },
    onSuccess: (data, paymentID) => {
      setAllUserPaymentDetails((prev) => prev.filter((item) => item.id !== paymentID));
      toast.success(data.response);
    },
    onError: (error) => {
      toast.error(error.message || "Something went wrong");
    },
  });

  const handleRefund = (payment_id) => {
    if (!window.confirm("Are you sure you want to refund this user?")) {
      window.alert("Cancelled, No Action Taken!");
      return;
    }
    refundMutation.mutate(payment_id);
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        overflow: "hidden",
        mb: 3,
      }}
    >
      {/* Section header */}
      <Box
        sx={{
          background: "#04343a",
          px: "20px",
          py: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
          Premium Users — Payments
        </Typography>
        <CSVLink data={allUserPaymentDetails} filename={"Premium Users.csv"} style={{ textDecoration: "none" }}>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              color: "#fff",
              fontWeight: 600,
              borderRadius: "7px",
              px: 2,
              fontSize: "12px",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Box>

      {/* Stats + Filters bar */}
      <Box
        sx={{
          px: "20px",
          py: "12px",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "nowrap",
          overflowX: "auto",
        }}
      >
        {/* Stats chips */}
        <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
          <Chip
            label={`Total: ${addCommaToCurrency(totalCount)}`}
            size="small"
            sx={{ bgcolor: "#e6f4f1", color: "#016551", fontWeight: 600, fontSize: "12px" }}
          />
          <Chip
            label={`Page: ${addCommaToCurrency(totalPageCount)}`}
            size="small"
            sx={{ bgcolor: "#f0f9ff", color: "#0369a1", fontWeight: 600, fontSize: "12px" }}
          />
          <Chip
            label={`₹ ${addCommaToCurrency(totalAmount)}`}
            size="small"
            sx={{ bgcolor: "#fef9c3", color: "#854d0e", fontWeight: 700, fontSize: "12px" }}
          />
        </Box>

        {/* Controls group */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Email, mobile or payment ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          sx={{ width: 240, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" } }}
        />
        <Button
          size="small"
          onClick={handleSearch}
          startIcon={<SearchIcon />}
          sx={{
            background: "linear-gradient(135deg, #016551, #014d3d)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "7px",
            px: 2,
            fontSize: "12px",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
          }}
        >
          Search
        </Button>
        <Button
          size="small"
          onClick={handleClearSearch}
          startIcon={<ClearIcon />}
          sx={{
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontWeight: 600,
            borderRadius: "7px",
            px: 2,
            fontSize: "12px",
            "&:hover": { bgcolor: "#f1f5f9" },
          }}
        >
          Clear
        </Button>

        {/* Date filter */}
        <TextField
          label="From"
          type="date"
          size="small"
          value={fromDate || ""}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 145, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" } }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={toDate || ""}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 145, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "13px" } }}
        />
        <Button
          size="small"
          disabled={!(fromDate && toDate)}
          onClick={filterData}
          sx={{
            background: "linear-gradient(135deg, #016551, #014d3d)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "7px",
            px: 2,
            fontSize: "12px",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            "&.Mui-disabled": { background: "#e2e8f0", color: "#9ca3af" },
          }}
        >
          Apply
        </Button>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            sx={{ color: "#04343a", "&:hover": { bgcolor: "#e6f4f1" } }}
            onClick={() => { setReload(true); setToDate(null); setFromDate(null); }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7", overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 1100,
            "& .MuiTableCell-root": {
              padding: "9px 12px",
              fontSize: "12px",
              borderBottom: "1px solid #f0f4f8",
              whiteSpace: "nowrap",
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              background: "#f0faf5",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 45 }}>S.No.</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 170 }}>Email</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Mobile</TableCell>
              <TableCell sx={{ ...headCellSx, width: 80 }}>Amount</TableCell>
              <TableCell sx={{ ...headCellSx, width: 100 }}>Pay Date</TableCell>
              <TableCell sx={{ ...headCellSx, width: 100 }}>Sub End</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 160 }}>Payment ID</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Invoice No.</TableCell>
              <TableCell sx={{ ...headCellSx, width: 90 }}>Total Alerts</TableCell>
              <TableCell sx={{ ...headCellSx, width: 90 }}>Used Alerts</TableCell>
              <TableCell sx={{ ...headCellSx, width: 75 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allUserPaymentDetails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5, color: "#94a3b8", fontSize: "13px" }}>
                  No payment records found.
                </TableCell>
              </TableRow>
            ) : (
              allUserPaymentDetails.map((row, index) => (
                <TableRow key={row.id} sx={{ "&:hover": { background: "#f0faf5" } }}>
                  <TableCell sx={{ color: "#94a3b8", fontSize: "12px" }}>{index + 1}</TableCell>
                  <TableCell sx={{ color: "#1e293b", fontSize: "12px" }}>{row.email}</TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.phoneno}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#016551" }}>
                      ₹{addCommaToCurrency(row.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.payment_date}</TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.payment_end_date}</TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.payment_id}</TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.invoice_numebr}</TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.total_alerts}</TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>{row.used_alerts}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleRefund(row.id)}
                      sx={{
                        background: "#fee2e2",
                        color: "#b91c1c",
                        fontWeight: 600,
                        fontSize: "11px",
                        borderRadius: "6px",
                        px: 1.5,
                        minWidth: "auto",
                        "&:hover": { background: "#fecaca" },
                      }}
                    >
                      Refund
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserPaymentsDashboardTable;
